/**
 * Notes as the scene draws them: text wrapped to the card, and the card's box.
 *
 * A note keeps the size the designer left it at (`size`). Its text wraps to
 * that width, and the card is at least that tall, growing only if the text
 * needs more room. A note without a size (written before sizes were kept) is
 * sized from its text.
 *
 * Text is measured with the browser's own font metrics where there is a
 * browser, so the card wraps where the editor's text area wrapped; the
 * editor sets the same font, size and padding. Elsewhere (tests, a server)
 * an average glyph width stands in. Either way it is a pure function of the
 * document on a given machine, and the screen and an export share the scene,
 * so they cannot differ (invariant 7).
 */
import type { TextNoteDocument } from "../design/document";
import { itemNumbers, parseNote, type Marks, type Run } from "../design/noteText";
import { THEME } from "../render/theme";
import type { SceneNote, SceneNoteLine, Size } from "./types";

export const NOTE_METRICS = {
  fontSize: 11,
  lineHeight: 15,
  padding: 7,
  /** Width of a note sized from its text, before it wraps. */
  autoWidth: 220,
  /** Average advance of a glyph, as a fraction of the font size, where nothing can measure. */
  glyphWidth: 0.56,
  minWidth: 36,
  /** A note's border, in scene units: inside the size, as `box-sizing: border-box`. */
  border: 1,
  /** How far list items are indented; their bullet or number sits in the gap. */
  indent: 16,
} as const;

/** Width of `text` in these marks at the note font, in scene units. */
export type Measure = (text: string, marks?: Marks) => number;

let context: CanvasRenderingContext2D | null | undefined;

export function noteFont(marks: Marks = {}, size: number = NOTE_METRICS.fontSize): string {
  return `${marks.italic ? "italic " : ""}${marks.bold ? "bold " : ""}${size}px ${THEME.fontFamily}`;
}

function browserMeasure(): Measure | null {
  if (context === undefined) {
    context = null;
    // jsdom has no canvas, and says so on the console; do not ask it.
    const real =
      typeof document !== "undefined" && typeof navigator !== "undefined" && !/jsdom/i.test(navigator.userAgent);
    if (real) {
      try {
        context = document.createElement("canvas").getContext("2d");
      } catch {
        context = null;
      }
    }
  }
  const measured = context;
  if (!measured) return null;
  return (text, marks = {}) => {
    measured.font = noteFont(marks);
    return measured.measureText(text).width;
  };
}

/** An average glyph width, bold a little wider: for tests, and anywhere nothing can measure. */
export const estimate: Measure = (text, marks = {}) =>
  text.length * NOTE_METRICS.fontSize * NOTE_METRICS.glyphWidth * (marks.bold ? 1.08 : 1);

export function defaultMeasure(): Measure {
  return browserMeasure() ?? estimate;
}

/** Room for text inside a card this wide. */
export function textWidth(cardWidth: number): number {
  return Math.max(1, cardWidth - 2 * (NOTE_METRICS.padding + NOTE_METRICS.border));
}

/** Height a card needs for this many lines. */
export function heightFor(lines: number): number {
  const { lineHeight, padding, border } = NOTE_METRICS;
  return Math.ceil(lines * lineHeight + 2 * (padding + border));
}

interface Word {
  pieces: Run[];
  /** The space before it, in the marks it was written in. */
  space: Run | null;
}

function marksOf(run: Marks): Marks {
  return { bold: run.bold, italic: run.italic, underline: run.underline, strike: run.strike };
}

/** Words across runs: a word may change marks part-way (`**bo**ld`) and stays one word. */
function wordsOf(runs: readonly Run[]): Word[] {
  const words: Word[] = [];
  let word: Word | null = null;
  let space: Run | null = null;
  for (const run of runs) {
    for (const part of run.text.split(/(\s+)/)) {
      if (!part) continue;
      if (/^\s+$/.test(part)) {
        word = null;
        space = { ...marksOf(run), text: " " };
        continue;
      }
      if (!word) {
        word = { pieces: [], space: words.length ? space : null };
        words.push(word);
        space = null;
      }
      word.pieces.push({ ...marksOf(run), text: part });
    }
  }
  return words;
}

function widthOf(pieces: readonly Run[], measure: Measure): number {
  return pieces.reduce((sum, piece) => sum + measure(piece.text, piece), 0);
}

function merge(pieces: readonly Run[]): Run[] {
  const out: Run[] = [];
  for (const piece of pieces) {
    const last = out[out.length - 1];
    const same =
      last &&
      Boolean(last.bold) === Boolean(piece.bold) &&
      Boolean(last.italic) === Boolean(piece.italic) &&
      Boolean(last.underline) === Boolean(piece.underline) &&
      Boolean(last.strike) === Boolean(piece.strike);
    if (same) last.text += piece.text;
    else out.push({ ...piece });
  }
  return out;
}

/** One paragraph or list item, wrapped to `width`: the runs of each line. */
export function wrapRuns(runs: readonly Run[], width: number, measure: Measure = defaultMeasure()): Run[][] {
  const lines: Run[][] = [];
  let line: Run[] = [];
  let used = 0;
  const push = () => {
    lines.push(merge(line));
    line = [];
    used = 0;
  };
  for (const word of wordsOf(runs)) {
    const wordWidth = widthOf(word.pieces, measure);
    const spaceWidth = word.space && line.length ? measure(" ", word.space) : 0;
    if (line.length && used + spaceWidth + wordWidth <= width) {
      if (word.space) line.push(word.space);
      line.push(...word.pieces);
      used += spaceWidth + wordWidth;
      continue;
    }
    if (line.length) push();
    if (wordWidth <= width) {
      line.push(...word.pieces);
      used = wordWidth;
      continue;
    }
    // Longer than a line: split between characters, as a text area does.
    for (const piece of word.pieces) {
      for (const character of piece.text) {
        const characterWidth = measure(character, piece);
        if (line.length && used + characterWidth > width) push();
        line.push({ ...marksOf(piece), text: character });
        used += characterWidth;
      }
    }
  }
  push();
  return lines;
}

/** A note's card: how big it is, and its text broken into drawn lines. */
export function layoutNote(
  note: Pick<TextNoteDocument, "text" | "size">,
  measure: Measure = defaultMeasure(),
): { lines: SceneNoteLine[]; size: Size } {
  const { autoWidth, minWidth, indent, padding, border } = NOTE_METRICS;
  const blocks = parseNote(note.text);
  const numbers = itemNumbers(blocks);
  const listed = (kind: string) => (kind === "paragraph" ? 0 : indent);
  let width = note.size?.width;
  if (width === undefined) {
    // Sized from the text: as wide as its longest line, up to the default width.
    const longest = Math.max(0, ...blocks.map((block) => widthOf(block.runs, measure) + listed(block.kind)));
    width = Math.min(autoWidth, Math.ceil(longest) + 2 * (padding + border) + 2);
  }
  width = Math.max(minWidth, width);
  const room = textWidth(width);
  const lines: SceneNoteLine[] = [];
  blocks.forEach((block, index) => {
    const offset = listed(block.kind);
    const marker = block.kind === "bullet" ? "•" : block.kind === "number" ? `${numbers[index]}.` : null;
    wrapRuns(block.runs, Math.max(1, room - offset), measure).forEach((runs, lineIndex) => {
      lines.push({ indent: offset, marker: lineIndex === 0 ? marker : null, runs });
    });
  });
  return { lines, size: { width, height: Math.max(note.size?.height ?? 0, heightFor(lines.length)) } };
}

export function buildNote(note: TextNoteDocument, drawnNodes: ReadonlySet<string>, measure?: Measure): SceneNote {
  const { lines, size } = layoutNote(note, measure);
  return {
    ref: note.local_id,
    text: note.text,
    lines,
    position: { x: note.position.x, y: note.position.y },
    size,
    anchor: note.anchor && drawnNodes.has(note.anchor) ? note.anchor : null,
  };
}
